/**
 * HomepageMobileBanner Component
 * Custom mobile banner for homepage with integrated navigation
 * Following the terminal glassmorphism design system
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, Sparkles, HelpCircle, Crown, Zap, Info } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import StarCoinsDisplay from '../starCoinSystem/StarCoinsDisplay';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { RANK_TIERS } from '../../userservices/userRankConfig';
import MobileSportsBar from './MobileSportsBar';

const HomepageMobileBanner = ({
  user,
  tier = 'basic',
  starCoins,
  showOnboarding,
  onStartTour
}) => {
  const navigate = useNavigate();
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const [userStats, setUserStats] = useState(null);
  const [showSupportTooltip, setShowSupportTooltip] = useState(false);
  const isBrazilian = getBrazilianStatus();

  // Translation helper
  const t = (text) => {
    const translations = {
      'Start Tour': isBrazilian ? 'Iniciar Tour' : 'Start Tour',
      'PRO': 'PRO',
      'BASIC': isBrazilian ? 'BÁSICO' : 'BASIC',
      'Tips': isBrazilian ? 'Dicas' : 'Tips'
    };
    return translations[text] || text;
  };

  // Handle hamburger menu click
  // Fetch user stats
  useEffect(() => {
    const fetchUserStats = async () => {
      if (user?.uid) {
        try {
          const userStatsRef = doc(db, 'userStats', user.uid);
          const userStatsDoc = await getDoc(userStatsRef);
          if (userStatsDoc.exists()) {
            setUserStats(userStatsDoc.data());
          }
        } catch (error) {
          console.error('Error fetching user stats:', error);
        }
      }
    };

    fetchUserStats();
  }, [user]);

  // Calculate level from rank
  const getUserLevel = () => {
    if (!userStats?.rank?.current) return 1;
    const rankIndex = RANK_TIERS.findIndex(tier => tier.name === userStats.rank.current);
    return rankIndex !== -1 ? rankIndex + 1 : 1; // Levels 1-25
  };

  // Get streak from userStats
  const getUserStreak = () => {
    return userStats?.points?.streak || 0;
  };

  const handleMenuClick = () => {
    if (typeof window.toggleMobileMenu === 'function') {
      window.toggleMobileMenu();
    }
  };

  return (
    <>
      <div className="w-full bg-black/90 backdrop-blur-lg border-b border-white/[0.05]">
        <div className="px-3 py-2">
        {/* Main Banner Row */}
        <div className="flex items-center justify-between">

          {/* Left Section: Hamburger Menu + App Name */}
          <div className="flex items-center gap-3">
            <button
              onClick={handleMenuClick}
              onMouseEnter={() => setIsMenuHovered(true)}
              onMouseLeave={() => setIsMenuHovered(false)}
              className="p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.05]
                         border border-white/[0.03] hover:border-white/[0.08]
                         transition-all duration-200 active:scale-95"
              aria-label="Menu"
            >
              <Menu className={`w-5 h-5 text-white/60 hover:text-white/80
                              transition-all duration-200 ${isMenuHovered ? 'scale-110' : ''}`} />
            </button>

            <h1
              onClick={() => navigate('/home')}
              className="text-[13px] font-mono font-medium tracking-[0.25em]
                         text-white/60 uppercase cursor-pointer
                         hover:text-white/80 transition-all"
            >
              PICKSTAR
            </h1>
          </div>

          {/* Center spacer */}
          <div className="flex-1"></div>

          {/* Right Section: Star Coins & Tour */}
          <div className="flex items-center gap-2">
            {/* Star Coins Display */}
            <StarCoinsDisplay
              userId={user?.uid}
              isMobile={true}
              compact={true}
              customStyles={{
                container: 'bg-white/[0.02] border-white/[0.05]',
                text: 'text-white/50',
                icon: 'text-emerald-400/60'
              }}
            />

            {/* Tour/Help Pills - Always show tour button */}
            <motion.button
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.5 }}
              onClick={() => {
                console.log('Tour button clicked, dispatching startOnboarding event');
                if (onStartTour) {
                  onStartTour();
                } else {
                  // Fallback: dispatch event directly
                  window.dispatchEvent(new CustomEvent('startOnboarding'));
                }
              }}
              className="group relative px-2.5 py-1 rounded-full
                         bg-gradient-to-r from-violet-500/[0.08] to-emerald-500/[0.08]
                         border border-white/[0.08] hover:border-white/[0.15]
                         transition-all duration-200 active:scale-95"
            >
              <div className="flex items-center gap-1.5">
                <HelpCircle className="w-3 h-3 text-violet-400/60 group-hover:text-violet-400/80" />
                <span className="font-mono text-[9px] text-violet-400/70 uppercase tracking-wider">
                  {t('Tour')}
                </span>
              </div>

              {/* Subtle pulse effect only if showOnboarding is true */}
              {showOnboarding && (
                <div className="absolute inset-0 rounded-full bg-violet-400/10 animate-ping" />
              )}
            </motion.button>

            {/* Support info (always visible) */}
            <div className="relative">
              <button
                onClick={() => setShowSupportTooltip(!showSupportTooltip)}
                className="relative p-1.5 rounded-lg bg-white/[0.02]
                           border border-white/[0.03] hover:border-white/[0.08]
                           hover:bg-white/[0.03] transition-all duration-200 active:scale-95"
                aria-label="Support"
              >
                <Info className="w-3.5 h-3.5 text-white/30 hover:text-white/50" />
                {/* Green indicator dot */}
                <div className="absolute -top-0.5 -right-0.5">
                  <span className="absolute inset-0 w-1.5 h-1.5 bg-emerald-400/40 rounded-full animate-ping" />
                  <span className="relative block w-1.5 h-1.5 bg-emerald-400/80 rounded-full" />
                </div>
              </button>

              {/* Premium minimal tooltip */}
              {showSupportTooltip && (
                <>
                  {/* Backdrop to close tooltip */}
                  <div
                    className="fixed inset-0 z-40"
                    onClick={() => setShowSupportTooltip(false)}
                  />

                  {/* Tooltip */}
                  <motion.div
                    initial={{ opacity: 0, scaleX: 0, originX: 1 }}
                    animate={{ opacity: 1, scaleX: 1 }}
                    exit={{ opacity: 0, scaleX: 0 }}
                    transition={{ duration: 0.2, ease: "easeOut" }}
                    className="absolute right-full top-1/2 -translate-y-1/2 mr-2 z-50"
                  >
                    <div className="px-3 py-2 rounded-lg bg-black/95 backdrop-blur-xl
                                    border border-white/[0.1] shadow-2xl min-w-[200px]">
                      <div className="flex flex-col gap-1">
                        <span className="text-[8px] font-mono text-white/30 uppercase tracking-wider">
                          Support
                        </span>
                        <a
                          href="mailto:support@pickstarai.com"
                          className="text-[10px] font-mono text-emerald-400/80 hover:text-emerald-400
                                     transition-colors"
                          onClick={(e) => e.stopPropagation()}
                        >
                          support@pickstarai.com
                        </a>
                      </div>
                    </div>
                  </motion.div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Optional: Quick Stats Row (can be toggled) */}
        {user && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            transition={{ delay: 0.2, duration: 0.3 }}
            className="mt-0.5 pt-0.5 border-t border-white/[0.03]"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {/* Mini stat pills */}
                <div className="flex items-center gap-1">
                  <span className="text-[9px] font-mono text-white/40 uppercase">Points:</span>
                  <span className="text-[10px] font-mono text-emerald-400/80">
                    {userStats?.points?.total?.toLocaleString() || 0}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[9px] font-mono text-white/40 uppercase">Level:</span>
                  <span className="text-[10px] font-mono text-violet-400/80">{getUserLevel()}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-[9px] font-mono text-white/40 uppercase">Streak:</span>
                  <span className="text-[10px] font-mono text-amber-400/80">{getUserStreak()}</span>
                </div>
              </div>

              {/* Quick action */}
              <button
                onClick={() => navigate('/chat')}
                className="px-2 py-0.5 rounded bg-emerald-500/10
                           border border-white/[0.05] hover:border-white/[0.1]
                           transition-all duration-200 active:scale-95"
              >
                <div className="flex items-center gap-1">
                  <span className="font-mono text-[9px] text-white/40 uppercase tracking-wider">
                    Chat AI
                  </span>
                </div>
              </button>
            </div>
          </motion.div>
        )}
        </div>
      </div>

      {/* Mobile Sports Navigation Bar */}
      <MobileSportsBar />
    </>
  );
};

export default HomepageMobileBanner;